import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { fetchWithAuth } from '../utils/apiHelper';

const ConfirmarTurno = () => {
  const { turno_id } = useParams();
  const turnoIds = turno_id.split(',').filter(Boolean);

  const [turnosInfo, setTurnosInfo] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({});
  const [notification, setNotification] = useState(null);

  useEffect(() => {
    const fetchTurnosInfo = async () => {
      try {
        const fetches = turnoIds.map(id =>
          fetchWithAuth(`/turnos/estado/turno/${id}`, { method: 'GET' })
            .then(response => response)
            .catch(error => {
              console.error(`Error fetching turno info for ${id}:`, error);
              return null;
            })
        );
        const results = await Promise.all(fetches);
        const validResults = results.filter(result => result);
        setTurnosInfo(validResults);
      } catch (error) {
        console.error('Error fetching turnos info:', error);
        setTurnosInfo([]);
      } finally {
        setLoading(false);
      }
    };

    fetchTurnosInfo();
  }, [turnoIds]);

  // Auto-dismiss notification after 3 seconds
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => setNotification(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const handleClientArrival = async (id) => {
    setActionLoading(prev => ({ ...prev, [id]: true }));
    try {
      await fetchWithAuth(`/turnos/cliente/confirmar/${id}`, { method: 'POST' });
      setTurnosInfo(prev =>
        prev.map(turno =>
          turno.turno_id === id ? { ...turno, estado: 'Confirmado' } : turno
        )
      );
      setNotification({ message: 'Turno confirmado correctamente', type: 'success' });
    } catch (error) {
      console.error(`Error confirming turno ${id}:`, error);
      setNotification({ message: 'Error al confirmar el turno', type: 'error' });
    } finally {
      setActionLoading(prev => ({ ...prev, [id]: false }));
    }
  };

  const handleCancel = async (id) => {
    setActionLoading(prev => ({ ...prev, [id]: true }));
    try {
      await fetchWithAuth(`/turnos/cliente/cancelar/${id}`, { method: 'POST' });
      setTurnosInfo(prev =>
        prev.map(turno =>
          turno.turno_id === id ? { ...turno, estado: 'Cancelado' } : turno
        )
      );
      setNotification({ message: 'Turno cancelado correctamente', type: 'success' });
    } catch (error) {
      console.error(`Error cancelling turno ${id}:`, error);
      setNotification({ message: 'Error al cancelar el turno', type: 'error' });
    } finally {
      setActionLoading(prev => ({ ...prev, [id]: false }));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-xl text-gray-700 animate-pulse">Cargando...</div>
      </div>
    );
  }

  if (turnosInfo.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-xl text-gray-700">No se encontraron turnos.</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-blue-100 to-purple-100 p-4 sm:p-6">
      {/* Notification */}
      {notification && (
        <div
          className={`w-full max-w-3xl mb-6 p-4 rounded-lg border flex items-center justify-between transition-opacity duration-300
          ${notification.type === 'success'
              ? 'bg-green-50 border-green-400 text-green-800'
              : 'bg-red-50 border-red-400 text-red-800'
            }`}
        >
          <span className="text-sm sm:text-base">{notification.message}</span>
          <button
            onClick={() => setNotification(null)}
            className="ml-4 text-2xl font-bold leading-none focus:outline-none"
          >
            &times;
          </button>
        </div>
      )}

      <div className="w-full max-w-3xl bg-white rounded-2xl shadow-lg p-6 sm:p-8 space-y-6">
        <h1 className="text-3xl sm:text-4xl font-extrabold text-center text-gray-800 bg-clip-text bg-gradient-to-r from-blue-500 to-purple-500">
          Confirmar Turno{turnosInfo.length > 1 ? 's' : ''}
        </h1>
        {turnosInfo.map((turno) => (
          <div
            key={turno.turno_id}
            className="border border-gray-200 rounded-xl p-4 sm:p-6 transition-shadow duration-300 hover:shadow-md"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <p className="text-base sm:text-lg font-medium text-gray-600">
                  Servicio:
                  <span className="ml-2 font-semibold text-gray-900">{turno.servicio}</span>
                </p>
                <p className="text-base sm:text-lg font-medium text-gray-600">
                  Fecha:
                  <span className="ml-2 font-semibold text-gray-900">
                    {new Date(turno.fecha).toLocaleDateString()}
                  </span>
                </p>
              </div>
              <div>
                <p className="text-base sm:text-lg font-medium text-gray-600">
                  Hora:
                  <span className="ml-2 font-semibold text-gray-900">{turno.hora}</span>
                </p>
                <p className="text-base sm:text-lg font-medium text-gray-600">
                  Estado:
                  <span
                    className={`ml-2 font-semibold ${
                      turno.estado === 'Confirmado'
                        ? 'text-green-600'
                        : turno.estado === 'Cancelado'
                        ? 'text-red-600'
                        : 'text-yellow-600'
                    }`}
                  >
                    {turno.estado}
                  </span>
                </p>
              </div>
            </div>
            {(turno.estado === 'Reservado' || turno.estado === 'Senado') && (
              <div className="mt-4 flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => handleClientArrival(turno.turno_id)}
                  disabled={actionLoading[turno.turno_id]}
                  className="flex-1 py-3 bg-green-500 text-white font-semibold rounded-lg shadow hover:bg-green-600 transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-green-300 disabled:opacity-50"
                >
                  {actionLoading[turno.turno_id] ? 'Procesando...' : 'Confirmar'}
                </button>
                <button
                  onClick={() => handleCancel(turno.turno_id)}
                  disabled={actionLoading[turno.turno_id]}
                  className="flex-1 py-3 bg-red-500 text-white font-semibold rounded-lg shadow hover:bg-red-600 transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-red-300 disabled:opacity-50"
                >
                  {actionLoading[turno.turno_id] ? 'Procesando...' : 'Cancelar'}
                </button>
              </div>
            )}
            {turno.estado === 'Confirmado' && (
              <p className="mt-4 text-center text-green-600 font-bold text-lg">
                El turno ha sido confirmado.
              </p>
            )}
            {turno.estado === 'Cancelado' && (
              <p className="mt-4 text-center text-red-600 font-bold text-lg">
                El turno ha sido cancelado.
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ConfirmarTurno;
